// FileItem <-> PickerResult 互转工具
function pickerResultToFileItem(p: PickerResult): FileItem {
  let ext = '';
  if (p.fileName) {
    const dotIdx = p.fileName.lastIndexOf('.');
    if (dotIdx > -1) ext = p.fileName.slice(dotIdx + 1).toLowerCase();
  }
  return {
    name: p.fileName || '',
    size: p.fileSize || 0,
    width: p.width,
    height: p.height,
    ext,
    url: p.path,
    mimeType: p.mimeType,
    thumbnailPath: p.thumbnailPath,
  };
}
function fileItemToPickerResult(f: FileItem): PickerResult {
  console.log('---> fileItemToPickerResult', f);
  let type: 'photo' | 'video' | 'doc' = 'doc';
  if (f.mimeType?.startsWith('image/')) type = 'photo';
  else if (f.mimeType?.startsWith('video/')) type = 'video';
  return {
    type,
    width: (f as any).width || 0,
    height: (f as any).height || 0,
    path: f.url,
    mimeType: f.mimeType || '',
    thumbnailPath: f.thumbnailPath,
    fileName: f.name,
    fileSize: f.size,
  };
}

import React, {
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
  useEffect,
} from "react";
import {
  View,
  TextInput,
  NativeSyntheticEvent,
  TextInputContentSizeChangeEventData,
  Animated,
  TextInputTextInputEventData,
  TextInputSelectionChangeEventData,
  Modal,
  Keyboard,
  Pressable,
  Text,
  TextInputKeyPressEventData,
  Image,
  StyleSheet,
  Platform,
} from "react-native";
import { ChatType, MessageType } from "../../constants/types";
import { getDisplayName, isIOS } from "../../utils/utils";
import { EmojiPickerModal } from "./EmojiPickerModal";
import { CollectPickerModal } from "./CollectPickerModal";
import { TopicUserView } from "./TopicUsers";
import { User } from "../../../imsdk/db/t_user";
import { chatInputStyles } from "./styles";
import {
  color_999999,
  color_BEBEBE,
  color_main,
  white,
  ColorF5F5F5,
} from "../../assets/style/colors";
import { ImageView } from "../../components/ImageView";
import { ImagePreviewModal, PreviewItem } from "../../components/ImagePreviewModal";
import {
  Str_Cancel,
  Str_Confirm,
  Str_EmptyNotSend,
  Str_InProhibition,
  Str_MaxFileCountLimit,
  Str_No_documents,
  Str_No_images,
  Str_No_videos,
  Str_PeopleChoose,
  Str_Send,
  Str_SendImage,
  Str_SendMsgPlaceholder,
} from "../../language/string_res";
import * as ImSdk from "../../../imsdk/api";
import { showDialog, showMsgDialog } from "../../components/CommonDialog";
import { PickerResult, openMultiplePicker } from "../../utils/PicOrCameraManager";
import { FileResponse } from "../../utils/upload";
import { handleMultipleFiles, handlePasteItems } from "../../utils/mediaFileUtils";
import EmojiIcon from "../../assets/svg/icon_emoji.svg";
import CollectIcon from "../../assets/svg/ic_collect.svg";
import FileIcon from "../../assets/svg/ic_file.svg";
import RNFS from "react-native-fs";
import { ClipboardUtil } from "../../utils/ClipboardUtils";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { FileItem, FilePreviewList } from "../../components/FilePreviewList";
import { showToast } from "../../components/Toast";

export type ChatPropsEvent = {
  content: string;
  type: MessageType;
  atUids?: number[];
  meta?: object;
};

export type ChatInputProps = {
  from: "ChatList" | "MassSending" | "EmojiComponent";
  showVideo: boolean;
  showImage: boolean;
  showCall: boolean;
  showFile: boolean;
  canSpeak: boolean;
  isHadQuote: boolean;
  onInputTypeChanged: (type: ActiveIcon) => void;
  onSubmit(res: ChatPropsEvent): void;
  onSubmitMedia(result: PickerResult[]): void;
  onInput?(event: ChatPropsEvent): void;
  onCallAudio?(): void;
  onCallVideo?(): void;
  topicId: string;
  type: ChatType;
  draft?: string;
  inputVal?: string; // 初始化输入框内容
  setInputVal?(val: string): void; // 初始化输入框内容回调
};

export type ActiveIcon = "emoji" | "input";

export type ForwardHandle = {
  focus: () => void;
  onTouch: () => void;
  addAtUser: (user: User) => void;
};

// 输入框草稿状态快照，用于保存和恢复用户的输入内容
type DraftSnapshot = {
  text: string;
  inputCus: number;
  currAtt: { name: string; uid: number }[];
  pendingFiles: FileItem[];
  activeType: ActiveIcon;
  imageSource: string;
};

const styles = StyleSheet.create({
  inputIconBox: {
    width: 34,
    height: 34,
    marginRight: 8,
    justifyContent: "center",
    alignItems: "center",
    overflow: "visible", // 确保图标不会被裁剪
    borderRadius: 6,
    backgroundColor: 'transparent',
  },
  inputIconBoxActive: {
    backgroundColor: ColorF5F5F5,
  },
});
// ChatInput 组件
export const ChatInput = forwardRef<ForwardHandle, ChatInputProps>(
  (props, refs) => {
    // 输入框高度固定，不再使用动画
    // input对象
    const inputRef = useRef<TextInput>(null);
    // 当前文本
    const [text, setText] = useState(props.draft || "");
    const textRef = useRef<string>();
    // 防止重复提交的标志
    const isSubmittingRef = useRef<boolean>(false);
    // 标记是否是表情输入（避免重置光标位置）
    const isEmojiInputRef = useRef<boolean>(false);
    const emojiCursorPosRef = useRef<number>(0);
    const emojiTextRef = useRef<string>("");
    // 当前组件类型
    const [activeType, updateActiveType] = useState<ActiveIcon>("input");
    // 当前input光标位置
    const [inputCus, setInputCus] = useState(0);
    const [isHadQuote, setIsHadQuote] = useState(props.isHadQuote);
    // 是否展示弹窗
    const [modalVisible, setModalVisible] = useState(false);
    // 当前选中被艾特的用户（手动从弹窗选中）
    const [currAtt, setCurrAtt] = useState<{ name: string; uid: number }[]>([]);
    const [imageSource, setImageSource] = useState<string>("");
    const pendingFilesCount = useRef<number>(0);
    // 合并所有待发送文件（图片/视频/文档）
    const [pendingFiles, setPendingFiles] = useState<FileItem[]>([]);
    const generatingThumbsRef = useRef<Set<number>>(new Set<number>());
    // 保存用户输入的草稿状态，每个tid对应一个快照记录，方便在切换会话后恢复
    const draftSnapshotRef = useRef<Record<string, DraftSnapshot>>(new Proxy({}, {
      get: (target, prop) => Reflect.get(target, prop),
      set: (target, prop, value) => Reflect.set(target, prop, value),
    }));
    const lastTidRef = useRef<string>(props.topicId); 

    // 预览 Modal 状态
    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewItems, setPreviewItems] = useState<PreviewItem[]>([]);
    const [previewIndex, setPreviewIndex] = useState(0);
    const lineHeight = 22;
    const showEmojiInput = activeType === "emoji";
    const [emojiIconPosition, setEmojiIconPosition] = useState<{ x: number; y: number; width: number; height: number } | undefined>();
    const [collectIconPosition, setCollectIconPosition] = useState<{ x: number; y: number; width: number; height: number } | undefined>();
    const [showCollectPicker, setShowCollectPicker] = useState(false);
    const emojiIconRef = useRef<View>(null);
    const collectIconRef = useRef<View>(null);
    const keyboardHeight = useRef(336);

    const [isTextInputFocus, setIsTextInputFocus] = useState(false);
    const inputContainerRef = useRef<View>(null);

    // 在 Web 平台为视频生成缩略图
    const createVideoThumbnail = async (videoUrl: string): Promise<string | undefined> => {
      if (Platform.OS !== 'web' || !videoUrl) return undefined;
      return new Promise((resolve) => {
        try {
          const video = document.createElement('video') as any;
          video.crossOrigin = 'anonymous';
          video.muted = true;
          video.playsInline = true;
          video.preload = 'auto';

          let objectUrl: string | null = null;
          // If it's not a http(s) or data URL, try to create object URL
          if (!/^https?:\/\//.test(videoUrl) && !videoUrl.startsWith('data:')) {
            try {
              // @ts-ignore
              const blob = null;
            } catch (e) { }
          }

          const cleanup = () => {
            try {
              video.src = '';
              video.removeAttribute('src');
            } catch (e) { }
            if (objectUrl) {
              URL.revokeObjectURL(objectUrl);
              objectUrl = null;
            }
          };

          const onError = () => {
            cleanup();
            resolve(undefined);
          };

          video.addEventListener('error', onError);

          const onLoadedMeta = () => {
            // seek to small position (0.1s) or middle if shorter
            const t = Math.min(0.1, (video.duration || 0) / 2);
            const seekHandler = () => {
              try {
                const canvas = document.createElement('canvas');
                const w = video.videoWidth || 160;
                const h = video.videoHeight || 90;
                canvas.width = w;
                canvas.height = h;
                const ctx = canvas.getContext('2d');
                if (ctx) {
                  ctx.drawImage(video, 0, 0, w, h);
                  const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
                  cleanup();
                  resolve(dataUrl);
                  return;
                }
              } catch (e) {
                // ignore
              }
              cleanup();
              resolve(undefined);
            };

            video.currentTime = t;
            video.addEventListener('seeked', seekHandler, { once: true });
          };

          video.addEventListener('loadedmetadata', onLoadedMeta, { once: true });

          // 设置 src（支持 data: URL 和 http(s)）
          video.src = videoUrl;
          // 尝试加载
          // some browsers require play() to be called to decode; but we avoid autoplay
        } catch (e) {
          resolve(undefined);
        }
      });
    };

    // 自动为 pendingFiles 中的视频生成缩略图并更新 state
    useEffect(() => {
      pendingFilesCount.current = pendingFiles.length;
      pendingFiles.forEach((item, idx) => {
        if (item.mimeType?.startsWith('video/') && !item.thumbnailPath && !generatingThumbsRef.current.has(idx)) {
          generatingThumbsRef.current.add(idx);
          (async () => {
            try {
              const thumb = await createVideoThumbnail(item.url || '');
              if (thumb) {
                setPendingFiles((prev) => prev.map((p, i) => i === idx ? ({ ...p, thumbnailPath: thumb }) : p));
              }
            } catch (e) {
              console.error('[ChatInput] createVideoThumbnail error', e);
            } finally {
              generatingThumbsRef.current.delete(idx);
            }
          })();
        }
      });
    }, [pendingFiles]);

    useEffect(() => {
      props.onInputTypeChanged?.(activeType);
    }, [activeType]);

    useEffect(() => {
      console.log('[ChatInput] props.isHadQuote changed', props.showImage, props.showVideo, props.showFile);
    }, [props]);

    // 监听页面切换，关闭所有选择器
    useEffect(() => {
      if (Platform.OS !== 'web') {
        return;
      }

      const handleVisibilityChange = () => {
        const doc: any = (global as any).document;
        if (doc?.hidden) {
          closeAllPickers();
        }
      };

      const handlePopState = () => {
        // 浏览器前进/后退时关闭选择器
        closeAllPickers();
      };

      const doc: any = (global as any).document;
      const win: any = (global as any).window;

      if (doc && win) {
        doc.addEventListener('visibilitychange', handleVisibilityChange);
        win.addEventListener('popstate', handlePopState);

        return () => {
          doc.removeEventListener('visibilitychange', handleVisibilityChange);
          win.removeEventListener('popstate', handlePopState);
        };
      }
    }, []);

    // 关闭所有选择器
    const closeAllPickers = () => {
      if (showEmojiInput) {
        setEmojiIconPosition(undefined);
        onUpdateActiveType("input");
      }
      if (showCollectPicker) {
        setCollectIconPosition(undefined);
        setShowCollectPicker(false);
      }
    };

    // 监听点击外部事件，关闭表情和收藏选择器
    useEffect(() => {
      if (Platform.OS !== 'web') {
        return;
      }

      // 监听点击外部区域（不包括输入框、表情图标、收藏图标）
      const handleClickOutside = (e: Event) => {
        const target = e.target as any;
        if (!target) return;

        // 检查点击是否在选择器、输入框、表情图标、收藏图标内部
        if (target.closest && typeof target.closest === 'function') {
          if (target.closest('[data-emoji-picker]') ||
            target.closest('[data-collect-picker]') ||
            target.closest('[data-chat-input]')) {
            return; // 点击在选择器或输入框内部，不关闭
          }
        }

        // 点击外部，关闭所有选择器
        closeAllPickers();
      };

      // 监听聊天列表点击
      const handleChatListClick = (e: Event) => {
        closeAllPickers();
      };

      if (showEmojiInput || showCollectPicker) {
        const doc: any = (global as any).document;

        if (doc) {
          // 添加点击监听（延迟执行，避免立即触发）
          setTimeout(() => {
            doc.addEventListener('mousedown', handleClickOutside, true);
            doc.addEventListener('touchstart', handleClickOutside, true);
          }, 100);

          // 监听聊天列表点击
          const chatListContainer = doc.querySelector('[data-chat-list]') ||
            doc.querySelector('.chat-list-container') ||
            doc.querySelector('[class*="ChatList"]') ||
            doc.querySelector('[class*="FlatList"]');

          if (chatListContainer) {
            chatListContainer.addEventListener('mousedown', handleChatListClick, true);
            chatListContainer.addEventListener('touchstart', handleChatListClick, true);
          }

          return () => {
            doc.removeEventListener('mousedown', handleClickOutside, true);
            doc.removeEventListener('touchstart', handleClickOutside, true);
            if (chatListContainer) {
              chatListContainer.removeEventListener('mousedown', handleChatListClick, true);
              chatListContainer.removeEventListener('touchstart', handleChatListClick, true);
            }
          };
        }
      }
    }, [showEmojiInput, showCollectPicker]);

    // Web 平台添加拖拽和粘贴事件监听
    useEffect(() => {
      if (Platform.OS !== 'web') {
        return;
      }

      let cleanup: (() => void) | null = null;
      let isSetup = false;

      // 使用延迟来确保 DOM 元素已经渲染
      const setupListeners = (): (() => void) => {
        if (isSetup) {
          return cleanup || (() => { });
        }

        const doc = (global as any).document;
        if (!doc) {
          return () => { };
        }

        // 查找输入框元素 - 使用更宽松的查找策略
        const findInputElement = (): any => {
          // 方法1: 通过 placeholder 查找
          const allInputs = doc.querySelectorAll('textarea, input') as any;
          for (let i = 0; i < allInputs.length; i++) {
            const input = allInputs[i];
            const placeholder = input.placeholder || input.getAttribute('placeholder') || '';
            if (placeholder.includes('发送') || placeholder.includes('Send') || placeholder.includes('消息')) {
              return input;
            }
          }

          // 方法2: 返回最后一个 textarea（通常是聊天输入框）
          const textareas = doc.querySelectorAll('textarea') as any;
          if (textareas.length > 0) {
            return textareas[textareas.length - 1];
          }

          return null;
        };

        const inputElement = findInputElement();
        const bodyElement = doc.body;

        if (!inputElement && !bodyElement) {
          return () => { };
        }

        // 添加拖拽事件处理器
        const dragOverHandler = (e: any) => {
          // 检查是否包含文件
          if (e.dataTransfer && e.dataTransfer.types) {
            const hasFiles = Array.from(e.dataTransfer.types).some((type: any) =>
              type === 'Files' || type === 'application/x-moz-file'
            );
            if (hasFiles) {
              e.preventDefault();
              e.stopPropagation();
              e.dataTransfer.dropEffect = 'copy';
            }
          }
        };

        const dropHandler = async (e: any) => {
          // 检查是否包含文件
          if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            e.preventDefault();
            e.stopPropagation();
            await handleDrop(e);
          }
        };

        // 添加粘贴事件处理器
        const pasteHandler = async (e: any) => {
          await handlePaste(e);
        };

        // 在输入框上添加事件（使用 capture 模式确保能捕获）
        if (inputElement) {
          inputElement.addEventListener('paste', pasteHandler, { capture: true, passive: false });
          inputElement.addEventListener('dragover', dragOverHandler, { capture: true, passive: false });
          inputElement.addEventListener('drop', dropHandler, { capture: true, passive: false });
        }

        // 在 window 上添加全局拖拽事件（作为后备，但只在输入框区域处理）
        const windowDropHandler = async (e: any) => {
          // 检查是否在输入框附近
          if (inputElement) {
            const rect = inputElement.getBoundingClientRect();
            const x = e.clientX || 0;
            const y = e.clientY || 0;

            // 扩大检测范围（输入框周围 100px）
            if (x >= rect.left - 100 && x <= rect.right + 100 &&
              y >= rect.top - 100 && y <= rect.bottom + 100) {
              if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                e.preventDefault();
                e.stopPropagation();
                await handleDrop(e);
              }
            }
          }
        };

        const windowDragOverHandler = (e: any) => {
          if (inputElement) {
            const rect = inputElement.getBoundingClientRect();
            const x = e.clientX || 0;
            const y = e.clientY || 0;

            if (x >= rect.left - 100 && x <= rect.right + 100 &&
              y >= rect.top - 100 && y <= rect.bottom + 100) {
              if (e.dataTransfer && e.dataTransfer.types) {
                const hasFiles = Array.from(e.dataTransfer.types).some((type: any) =>
                  type === 'Files' || type === 'application/x-moz-file'
                );
                if (hasFiles) {
                  e.preventDefault();
                  e.stopPropagation();
                  e.dataTransfer.dropEffect = 'copy';
                }
              }
            }
          }
        };

        const win = (global as any).window;
        if (win) {
          win.addEventListener('drop', windowDropHandler, { capture: true, passive: false });
          win.addEventListener('dragover', windowDragOverHandler, { capture: true, passive: false });
        }

        isSetup = true;

        return () => {
          if (inputElement) {
            inputElement.removeEventListener('paste', pasteHandler, { capture: true } as any);
            inputElement.removeEventListener('dragover', dragOverHandler, { capture: true } as any);
            inputElement.removeEventListener('drop', dropHandler, { capture: true } as any);
          }
          if (win) {
            win.removeEventListener('drop', windowDropHandler, { capture: true } as any);
            win.removeEventListener('dragover', windowDragOverHandler, { capture: true } as any);
          }
          isSetup = false;
        };
      };

      // 延迟设置，确保 DOM 已渲染
      const timeoutId = setTimeout(() => {
        cleanup = setupListeners();
      }, 1000);

      // 定期检查并设置（最多尝试 10 次）
      let attempts = 0;
      const maxAttempts = 10;
      const intervalId = setInterval(() => {
        attempts++;
        const doc = (global as any).document;
        if (doc && !isSetup && attempts < maxAttempts) {
          const input = doc.querySelector('textarea') as any;
          if (input) {
            clearInterval(intervalId);
            clearTimeout(timeoutId);
            cleanup = setupListeners();
          }
        } else if (attempts >= maxAttempts) {
          clearInterval(intervalId);
        }
      }, 300);

      return () => {
        clearTimeout(timeoutId);
        clearInterval(intervalId);
        if (cleanup) {
          cleanup();
        }
      };
    }, []); // 只在组件挂载时设置一次

    useEffect(() => {
      textRef.current = text;
    }, [text]);

    useEffect(() => {
      setIsHadQuote(props.isHadQuote);
    }, [props.isHadQuote]);

    // 更多功能已移除

    function onUpdateActiveType(type: ActiveIcon) {
      updateActiveType(type);
      props.onInputTypeChanged?.(type);
      setIsTextInputFocus(inputRef.current?.isFocused() === true);
    }

    useEffect(() => {
      // iOS 监听键盘事件
      const showSubscription = Keyboard.addListener(
        isIOS ? "keyboardWillShow" : "keyboardDidShow",
        (e) => {
          const height = e.endCoordinates.height;
          if (keyboardHeight.current !== height) {
            keyboardHeight.current = height;
          }
        }
      );

      return () => {
        showSubscription.remove();
      };
    }, []);

    useEffect(() => {
      if (props.inputVal && props.inputVal.length) {
        handleText(props.inputVal);
        props.setInputVal?.("");
      }
    }, [props.inputVal]);

    function onContentSizeChange(
      e: NativeSyntheticEvent<TextInputContentSizeChangeEventData>
    ) {
      // 输入框高度已固定，不再需要处理内容大小变化
    }

    function onChangeText(input: string) {
      // 如果是表情输入，保持光标位置
      if (isEmojiInputRef.current) {
        isEmojiInputRef.current = false;
        const cursorPos = emojiCursorPosRef.current;

        setText(input);
        setInputCus(cursorPos);

        // 更新 ref
        textRef.current = input;

        // 延迟设置光标位置，确保文本已更新
        setTimeout(() => {
          if (Platform.OS === 'web' && inputRef.current) {
            try {
              const inputEl = inputRef.current as any;
              // 尝试多种方式获取 DOM 元素
              let domNode: any = null;

              // 方法1: 通过 React Native Web 的内部结构
              if (inputEl._internalFiberInstanceHandleDEV?.stateNode?.node) {
                domNode = inputEl._internalFiberInstanceHandleDEV.stateNode.node;
              }
              // 方法2: 通过 _nativeNode
              else if (inputEl._nativeNode) {
                domNode = inputEl._nativeNode;
              }
              // 方法3: 直接查找 DOM
              else {
                const doc: any = (global as any).document;
                if (doc) {
                  const allInputs = doc.querySelectorAll('textarea, input[type="text"]');
                  for (let i = 0; i < allInputs.length; i++) {
                    const el = allInputs[i] as any;
                    if (el.placeholder === Str_SendMsgPlaceholder || el.value === input) {
                      domNode = el;
                      break;
                    }
                  }
                }
              }

              if (domNode) {
                // 确保值已更新
                if (domNode.value !== input) {
                  domNode.value = input;
                  // 触发 input 事件
                  const event = new Event('input', { bubbles: true });
                  Object.defineProperty(event, 'target', { value: domNode, enumerable: true });
                  domNode.dispatchEvent(event);
                }

                if (domNode.setSelectionRange) {
                  domNode.focus();
                  domNode.setSelectionRange(cursorPos, cursorPos);
                } else if (domNode.selectionStart !== undefined) {
                  domNode.focus();
                  domNode.selectionStart = cursorPos;
                  domNode.selectionEnd = cursorPos;
                }
              }
            } catch (e) {
              // Error setting cursor
            }
          } else if (Platform.OS !== 'web' && inputRef.current?.setNativeProps) {
            inputRef.current.setNativeProps({
              selection: { start: cursorPos, end: cursorPos }
            });
          }
        }, 50);

        // 触发 onInput 事件
        props.onInput?.({
          content: input,
          type: MessageType.TEXT,
        });
        return;
      }
      handleText(input);
    }

    function onDel() {
      const characters = Array.from(text);

      if (characters.length > 0) {
        // 获取最后一个字符
        const lastChar = characters[characters.length - 1];

        // 检查最后一个字符是否为表情符号
        // 更新文本，删除字符或整个表情
        // 这里可以使用更广泛的表情匹配
        // 使用 Unicode 属性匹配表情符号
        const isEmoji = lastChar.match(/\p{Emoji}/u);
        if (isEmoji) {
          // 删除整个表情符号
          const newText = characters.slice(0, characters.length - 1).join("");
          handleText(newText);
        } else {
          // 删除最后一个普通字符
          const newText = characters.slice(0, characters.length - 1).join("");
          handleText(newText);
        }
      }
    }

    useEffect(() => {
      if (props.topicId !== lastTidRef.current) {
        // 保存当前会话的草稿状态
        if (lastTidRef.current) {
          storeDraftToSnapshot();
        }
        // 恢复新会话的草稿状态
        restoreDraftFromSnapshot();

        lastTidRef.current = props.topicId;
      }
      // 组件卸载时保存草稿
      return () => {
        if (props.topicId.length > 0) {
          const draft = textRef.current || "";
          ImSdk.getInstance().SetDraft(props.topicId, draft);
        }
      };
    }, [props.topicId]);

    const handleText = (str: string) => {
      if (str.length > 1000) {
        str = truncateString(str);
      }

      setText(str);
      setInputCus(str.length);
    };
    const truncateString = (str: string, maxLength: number = 1000) => {
      let count = 0;
      let result = "";
      let i = 0;

      while (i < str.length && count < maxLength) {
        const code = str.codePointAt(i); // 获取当前字符的 Unicode 码点

        // 判断是否为高代理项（Emoji 的前半部分）
        if (code && code >= 0xd800 && code <= 0xdbff) {
          // 检查下一个字符是否为低代理项（Emoji 的后半部分）
          const nextCode = str.codePointAt(i + 1);
          if (nextCode && nextCode >= 0xdc00 && nextCode <= 0xdfff) {
            // 完整 Emoji，占用 2 个字符
            result += str[i] + str[i + 1];
            count += 2;
            i += 2; // 跳过低代理项
          } else {
            // 孤立的高代理项（无效字符），按 1 字符处理
            result += str[i];
            count += 1;
            i += 1;
          }
        } else {
          // 普通字符，按 1 字符处理
          result += str[i];
          count += 1;
          i += 1;
        }
      }

      return result;
    };

    const handleKeyPress = (
      e: NativeSyntheticEvent<TextInputKeyPressEventData>
    ) => {
      // Web 环境下，Enter 发送，Shift+Enter 换行
      if (e.nativeEvent.key === "Enter") {
        // 检查是否按下了 Shift 键（在 Web 上通过 nativeEvent.shiftKey 判断）
        const isShiftPressed = (e.nativeEvent as any).shiftKey === true;
        if (!isShiftPressed) {
          e.preventDefault?.(); // 阻止默认换行行为
          onSubmitEditing();
        }
        // 如果按下了 Shift，允许默认的换行行为
      } else if (e.nativeEvent.key === "@") {
        if (props.type === ChatType.GROUP) {
          // 群聊并且开始了@
          setModalVisible(true);
        }
      }
    };

    function fixAtContent(content: string): string {
      // 将没有空格的@符号前添加空格
      // @前面如果不是空格，就插入一个空格
      return content.replace(/([^ ])@/g, '$1 @');
    }

    function onSubmitEditing() {
      // 防止重复提交：如果正在提交中，直接返回
      // 这样可以避免在输入框清空前用户快速点击导致的重复发送
      if (isSubmittingRef.current) {
        return;
      }

      const content = text.trim();
      const hasFiles = pendingFiles.length > 0;
      // 如果既没有文字也没有文件，不发送
      if (content.length <= 0 && !hasFiles) {
        handleText("");
        Keyboard.dismiss();
        showMsgDialog(Str_EmptyNotSend);
        return;
      }
      // 设置提交标志，防止重复提交
      isSubmittingRef.current = true;
      // 先清空输入框和文件预览，避免用户看到内容
      inputRef.current?.clear();
      handleText("");
      const selectedAtt = currAtt.filter((item) => content.includes(`@${item.name}`));
      const filesToSend = [...pendingFiles];
      setPendingFiles([]);
      setCurrAtt([]);
      // 发送所有文件（每条单独发一条消息，自动转为 PickerResult）
      const sendFiles = async () => {
        for (let i = 0; i < filesToSend.length; i++) {
          const file = filesToSend[i];
          console.log('[ChatInput] Sending file', file);
          await new Promise<void>((resolve) => {
            props.onSubmitMedia?.([fileItemToPickerResult(file)]);
            setTimeout(() => resolve(), 200);
          });
        }
      };

      // 如果有文字，再发送文字消息
      const sendText = async () => {
        if (content.length > 0) {
          const submitInfo = {} as ChatPropsEvent;
          submitInfo.type = MessageType.TEXT;
          if (selectedAtt.length) {
            submitInfo.atUids = selectedAtt.map((item) => item.uid);
            // fixAtContent 保证每个@前都有一个空格，避免发送后消息内容显示错乱
            submitInfo.content = (submitInfo.atUids.length > 0 ? fixAtContent(content) : content).trim();
          } else {
            submitInfo.content = content;
          }
          await Promise.resolve(props.onSubmit?.(submitInfo));
        }
      };

      // 顺序：文件 -> 文字
      sendFiles()
        .then(() => sendText())
        .finally(() => {
          setTimeout(() => {
            isSubmittingRef.current = false;
          }, 100);
        });
    }

    function handleInputEmoji(emoji: string) {
      const currentText = textRef.current || text;
      const currentCursor = inputCus;
      let str = currentText.slice(0, currentCursor) + emoji + currentText.slice(currentCursor);
      const newCursorPos = currentCursor + emoji.length;

      // 直接设置文本，不通过 handleText（避免覆盖光标位置）
      if (str.length > 1000) {
        str = truncateString(str);
      }

      // 设置标志，标记这是表情输入
      isEmojiInputRef.current = true;
      emojiCursorPosRef.current = newCursorPos;

      // 更新 ref
      textRef.current = str;

      // 通过 onChangeText 更新文本（这样 TextInput 会正确更新）
      onChangeText(str);

      // 不关闭选择器，让用户可以继续选择表情
    }

    function handleInputPicture(res: FileResponse) {
      console.log('[ChatInput] handleInputPicture called', res);

      // 收藏的图片都是网络图片，需要直接发送
      // 检查是否是网络 URL（包括 http://、https://，以及可能的相对路径）
      const url = res.url || '';
      console.log('[ChatInput] URL:', url);

      const isNetworkUrl = url && (
        url.startsWith('http://') ||
        url.startsWith('https://') ||
        url.startsWith('//') || // 协议相对 URL
        (url.length > 0 && !url.startsWith('file://') && !url.startsWith('data:') && !url.startsWith('/'))
      );

      console.log('[ChatInput] isNetworkUrl:', isNetworkUrl);

      if (isNetworkUrl) {
        // 对于网络图片，直接构造消息并发送，不需要上传
        // 确保 URL 是完整的（如果是协议相对 URL，添加 https://）
        let finalUrl = url;
        if (url.startsWith('//')) {
          finalUrl = 'https:' + url;
        }

        console.log('[ChatInput] Sending network image:', finalUrl);

        const ev: ChatPropsEvent = {
          content: finalUrl,
          type: res.type,
          meta: {
            width: (res.width || 0).toString(),
            height: (res.height || 0).toString(),
            mimeType: res.mimeType || 'image/gif',
          },
        };

        console.log('[ChatInput] Event:', ev);
        console.log('[ChatInput] props.onSubmit exists:', !!props.onSubmit);

        if (props.onSubmit) {
          props.onSubmit(ev);
        } else {
          console.error('[ChatInput] props.onSubmit is not defined!');
        }
      } else {
        console.log('[ChatInput] Treating as local image, converting to PickerResult');
        // 对于本地图片或 data URL，转换为 PickerResult 格式，调用 onSubmitMedia
        const pickerResult: PickerResult = {
          type: res.type === MessageType.IMAGE ? 'photo' : 'video',
          width: res.width || 0,
          height: res.height || 0,
          path: url,
          mimeType: res.mimeType || 'image/gif',
        };

        console.log('[ChatInput] PickerResult:', pickerResult);
        console.log('[ChatInput] props.onSubmitMedia exists:', !!props.onSubmitMedia);

        if (props.onSubmitMedia) {
          props.onSubmitMedia([pickerResult]);
        } else {
          console.error('[ChatInput] props.onSubmitMedia is not defined!');
        }
      }

      // 关闭收藏选择器
      closeAllPickers();
    }

    function onPressInputIcon(type: ActiveIcon) {
      // 重复点击emoji, 收起emoji键盘
      if (type === "emoji" && type === activeType) {
        onUpdateActiveType("input");
        return;
      }

      onUpdateActiveType(type);
      switch (type) {
        case "emoji":
          inputRef.current?.blur();
          return;
        default:
          return;
      }
    }

    // 文字输入时储存当前光标
    function setInputNativeSelection(
      e: NativeSyntheticEvent<TextInputTextInputEventData>
    ) {
      setInputCus(e.nativeEvent.range.start || 0);
      if (e.nativeEvent.text === "@" && props.type === ChatType.GROUP) {
        // 群聊并且开始了@
        setModalVisible(true);
      }
    }

    // 变更选中时储存当前光标位置
    function handleSelectionChange(
      e: NativeSyntheticEvent<TextInputSelectionChangeEventData>
    ) {
      setInputCus(e.nativeEvent.selection.start || 0);
    }

    // 选中被艾特的人
    function selectedAttUser(user: User, addAtChar = "") {
      const userName = user?.nick || user?.name || user.employee;
      const fixName = addAtChar + userName + " ";
      setCurrAtt(currAtt.concat({ name: userName, uid: user.uid }));
      handleText(text + fixName);
      setModalVisible(false);
      inputRef.current?.focus();
    }

    function onTextInputFocus() {
      // 输入框获得焦点时，关闭所有选择器
      closeAllPickers();
      setIsTextInputFocus(true);
      // 是否允许发送图片
      if (props.showImage !== true) {
        return;
      }
      // 检查剪切板是否有图片
      checkIsClipboardHasImage();
    }

    function onTextInputBlur() {
      setIsTextInputFocus(false);
    }

    async function checkIsClipboardHasImage() {
      ClipboardUtil.hasImage().then((isHad) => {
        ClipboardUtil.getImageJPG().then((base64Image) => {
          if (!base64Image || base64Image.length <= 0) {
            return;
          }

          if (!base64Image.startsWith("data:image")) {
            base64Image = `data:image/jpeg;base64,${base64Image}`;
          }

          let url = base64Image.replace(/[\r\n]/g, "");
          setImageSource(url);

          // 8秒后隐藏
          setTimeout(() => {
            ClipboardUtil.hasImage().then((isHad) => {
              if (isHad) {
                ClipboardUtil.setString("");
                setImageSource("");
              }
            });
          }, 8000);
        });
      });
    }

    async function handleSendClipboardImage(imgUri: string) {
      if (props.showImage !== true) {
        showToast(Str_No_images);
        return;
      }
      const isHadImg = await ClipboardUtil.hasImage();
      if (isHadImg) {
        ClipboardUtil.setString("");
        setImageSource("");
      }

      showDialog({
        message: Str_SendImage,
        cancelText: Str_Cancel,
        confirmText: Str_Confirm,
        onCancel: () => { },
        onConfirm: () => {
          saveClipboardImage(imgUri).then((res: PickerResult | undefined) => {
            if (res != undefined) {
              // 这里是否需要走 MoreInput内部的OnPicture ??
              props.onSubmitMedia?.([res]);
            }
          });
        },
      });
    }





    // 处理拖拽事件
    const handleDragOver = (e: any) => {
      if (Platform.OS === 'web') {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const handleDragLeave = (e: any) => {
      if (Platform.OS === 'web') {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const handleDrop = async (e: DragEvent | any) => {
      if (Platform.OS !== 'web') {
        return;
      }
      console.log('[ChatInput] handleDrop called', props.showImage, props.showVideo, props.showFile);
      e.preventDefault();
      e.stopPropagation();
      const dataTransfer = e.dataTransfer || e.nativeEvent?.dataTransfer;
      if (!dataTransfer) {
        return;
      }
      const files = Array.from(dataTransfer.files || []) as File[];
      const rawItems = await handleMultipleFiles(files);
      const fileItems: FileItem[] = [];
      let deniedImage = false;
      let deniedVideo = false;
      let deniedDoc = false;
      for (const item of rawItems) {
        const mime = item.mimeType || '';
        if (mime.startsWith('image/')) {
          if (props.showImage) fileItems.push(item);
          else deniedImage = true;
        } else if (mime.startsWith('video/')) {
          if (props.showVideo) fileItems.push(item);
          else deniedVideo = true;
        } else {
          if (props.showFile) fileItems.push(item);
          else deniedDoc = true;
        }
      }
      if (deniedImage) showToast(Str_No_images);
      if (deniedVideo) showToast(Str_No_videos);
      if (deniedDoc) showToast(Str_No_documents);

      let count = pendingFilesCount.current;
      console.log('[ChatInput] handleDrop current pending count:', count);
      if (fileItems.length > 0) {
        if ((count + fileItems.length) > 9) {
          const allowedCount = 9 - count;
          setPendingFiles((prev) => [...prev, ...fileItems.slice(0, allowedCount)]);
        } else {
          setPendingFiles((prev) => [...prev, ...fileItems]);
        }

        count += fileItems.length;
      }

      inputRef.current?.focus();

      if (count > 9) {
        showMsgDialog(Str_MaxFileCountLimit);
      }

    };

    // 处理粘贴事件
    const handlePaste = async (e: ClipboardEvent | any) => {
    
      const clipboardData = e.clipboardData || (e.nativeEvent && e.nativeEvent.clipboardData);
      if (!clipboardData) {
        return;
      }

      if (pendingFilesCount.current >= 9) {
        showMsgDialog(Str_MaxFileCountLimit);
        return;
      }

      const items = Array.from(clipboardData.items || []) as DataTransferItem[];
      const rawItems = await handlePasteItems(items);
      const fileItems: FileItem[] = [];
      let deniedImage = false;
      let deniedVideo = false;
      for (const item of rawItems) {
        const mime = item.mimeType || '';
        if (mime.startsWith('image/')) {
          if (props.showImage) fileItems.push(item);
          else deniedImage = true;
        } else if (mime.startsWith('video/')) {
          if (props.showVideo) fileItems.push(item);
          else deniedVideo = true;
        }
      }
      if (deniedImage) showToast(Str_No_images);
      if (deniedVideo) showToast(Str_No_videos);
      if (fileItems.length > 0) {
        const canPickCount = 9 - pendingFilesCount.current;
        if (canPickCount <= 0) {
          showMsgDialog(Str_MaxFileCountLimit);
          return;
        }
        setPendingFiles((prev) => [...prev, ...fileItems.slice(0, canPickCount)]);
        if (fileItems.length > canPickCount) {
          showMsgDialog(Str_MaxFileCountLimit);
        }
        inputRef.current?.focus();
      }
    };

    async function saveClipboardImage(
      imgUri: string
    ): Promise<PickerResult | undefined> {
      try {
        // 参数验证
        if (!imgUri || typeof imgUri !== "string") {
          return undefined;
        }

        // 检查是否是 Base64 data URL
        if (!imgUri.startsWith("data:image")) {
          return undefined;
        }

        // 提取 Base64 数据和图片类型
        const matches = imgUri.match(/^data:image\/(\w+);base64,(.+)$/);
        if (!matches || matches.length !== 3) {
          return undefined;
        }

        const imageType = matches[1];
        const base64Data = matches[2];

        // 生成唯一的文件名
        const timestamp = Date.now();
        const randomStr = Math.random().toString(36).substring(2, 8);
        const fileName = `clipboard_${timestamp}_${randomStr}.${imageType}`;
        const filePath = `${RNFS.TemporaryDirectoryPath}/${fileName}`;
        const fileUri = `file://${filePath}`;

        // 将 Base64 字符串写入文件
        await RNFS.writeFile(filePath, base64Data, "base64");

        // 验证文件是否成功创建
        const fileExists = await RNFS.exists(filePath);
        if (!fileExists) {
          return undefined;
        }

        // 获取文件信息
        const fileInfo = await RNFS.stat(filePath);

        // 获取图片尺寸
        const dimensions = await getImageDimensions(fileUri);

        return {
          path: filePath,
          width: dimensions.width,
          height: dimensions.height,
          type: "photo",
          mimeType: `image/${imageType}`,
        };
      } catch (error) {
        console.error("❌ 保存剪贴板图片失败:", error);
        return undefined;
      }
    }

    // 获取图片尺寸的函数
    async function getImageDimensions(
      uri: string
    ): Promise<{ width: number; height: number }> {
      return new Promise((resolve, reject) => {
        Image.getSize(
          uri,
          (width, height) => {
            resolve({ width, height });
          },
          (error) => {
            console.error("获取图片尺寸失败:", error);
            // 如果获取尺寸失败，返回默认值
            resolve({ width: 0, height: 0 });
          }
        );
      });
    }

    const storeDraftToSnapshot = () => {
      // 在重置前保存当前的所有状态到草稿快照，以tid作为key保存，便于切换会话后恢复
      // 这样每个会话都能独立保存和恢复自己的输入内容
      const tidKey = lastTidRef.current;
      if (tidKey && text.length > 0) {
        draftSnapshotRef.current[tidKey] = {
          text,
          inputCus,
          currAtt: [...currAtt],
          pendingFiles: [...pendingFiles],
          activeType,
          imageSource,
        };
      }
    }

    const restoreDraftFromSnapshot = () => {
      // 尝试从保存的草稿快照中恢复状态
      // 使用topicId作为key查找对应的快照，如果没有保存的状态，则返回false
      const tidKey = props.topicId;
      if (!tidKey || !draftSnapshotRef.current[tidKey]) {
    // 恢复所有状态到初始值，清空输入框内容、图片预览、@用户列表等
    // 保持模态框、加载标志等内部状态一致
        setText("");
        setInputCus(0);
        isSubmittingRef.current = false;
        isEmojiInputRef.current = false;
        emojiCursorPosRef.current = 0;
        emojiTextRef.current = "";
        textRef.current = "";
        setPendingFiles([]);
        updateActiveType("input");
        setModalVisible(false);
        setCurrAtt([]);
        setImageSource("");
        generatingThumbsRef.current.clear();
        return;
      }

      const snapshot = draftSnapshotRef.current[tidKey];
      setText(snapshot.text);
      setInputCus(snapshot.inputCus);
      setCurrAtt(snapshot.currAtt);
      setPendingFiles(snapshot.pendingFiles);
      updateActiveType(snapshot.activeType);
      setImageSource(snapshot.imageSource);
      textRef.current = snapshot.text;

      // 恢复后删除该tid的快照，防止重复恢复
      delete draftSnapshotRef.current[tidKey];
    }

    useImperativeHandle(refs, () => ({
      focus() {
        inputRef.current?.focus();
      },
      addAtUser(user: User) {
        selectedAttUser(user, "@");
      },
      onTouch() {
        if (activeType !== "input") {
          onUpdateActiveType("input");
        }
      }
    }));

    // 录音功能已移除
    // const clickSpeech = () => {
    //   if (activeType === "voice") {
    //     onUpdateActiveType("input");
    //   } else {
    //     requestAudio({
    //       suc: () => {
    //         onUpdateActiveType("voice");
    //       },
    //     });
    //   }
    // };

    if (!props.canSpeak) {
      return (
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            paddingVertical: 8,
            justifyContent: "center",
            backgroundColor: white,
            height: 40,
          }}
        >
          <Text style={{ color: color_999999, fontSize: 16 }}>
            {Str_InProhibition}
          </Text>
        </View>
      );
    }

    const onFilePicker = async () => {
      closeAllPickers();
      console.log('[ChatInput] onFilePicker called', props.showImage, props.showVideo, props.showFile);
      if (!props.showImage && !props.showVideo && !props.showFile) {
        return;
      }
      const canPickCount = 9 - pendingFilesCount.current;
      console.log('[ChatInput] canPickCount:', canPickCount, pendingFilesCount.current);
      if (canPickCount <= 0) {
        showMsgDialog(Str_MaxFileCountLimit);
        return;
      }
      try {
        // 打开多选文件选择器，支持图片、视频、文档，最多选择9个
        const results = await openMultiplePicker('any', canPickCount);
        if (results && results.length > 0) {
          const imgRes: PickerResult[] = [];
          const videoRes: PickerResult[] = [];
          const filesRes: FileItem[] = [];
          for (const r of results) {
            console.log('[ChatInput] Picker result:', r);
            const fileObj = (r as any).file as File;
            const name = r.fileName ?? '';
            const size = r.fileSize ?? 0;
            const ext = name.split('.').pop()?.toLowerCase() || '';
            const mimeType = r.mimeType;
            const isImage = mimeType.startsWith('image/');
            const isVideo = mimeType.startsWith('video/');
            const url = URL.createObjectURL(fileObj);

            if (isImage) {
              imgRes.push({
                ...r,
                path: url,
                mimeType,
              });
            } else if (isVideo) {
              videoRes.push({
                ...r,
                path: url,
                mimeType,
              });
            } else {
              filesRes.push({ name, size, ext, url, mimeType });
            }
          }

          if (imgRes.length > 0) {
            if (props.showImage !== true) {
              showToast(Str_No_images);
            } else {
              setPendingFiles(prev => [
                ...prev,
                ...imgRes.map(p => pickerResultToFileItem(p as PickerResult))
              ]);
            }
          }
          if (videoRes.length > 0) {
            if (props.showVideo !== true) {
              showToast(Str_No_videos);
            } else {
              setPendingFiles(prev => [
                ...prev,
                ...videoRes.map(p => pickerResultToFileItem(p as PickerResult))
              ]);
            }
          }
          if (filesRes.length > 0) {
            if (props.showFile !== true) {
              showToast(Str_No_documents);
            } else {
              setPendingFiles(prev => [...prev, ...filesRes]);
            }
          }
        }
      } catch (error) {
        console.error('[ChatInput] Error selecting multiple files:', error);
      }
    }

    return (
      <View style={{ overflow: "visible", position: 'relative' }}>
        {/* 剪贴板图片预览（旧功能，保留兼容） */}
        {imageSource.length > 0 && (
          <ImageView
            source={{ uri: imageSource }}
            style={{
              width: 80,
              height: 80,
              borderRadius: 4,
              overflow: "visible",
              backgroundColor: "#f0f0f0",
              borderColor: "#ccc",
              position: "absolute",
              right: 10,
              top: isHadQuote ? -130 : -90,
            }}
            imgStyle={{ resizeMode: "contain", width: 80, height: 80 }}
            onClick={() => {
              handleSendClipboardImage(imageSource);
            }}
          />
        )}

        {/* 合并所有类型的预览为一排横滑 */}
        {pendingFiles.length > 0 && (
          <FilePreviewList
            files={pendingFiles}
            onRemoveFile={idx => setPendingFiles(prev => prev.filter((_, i) => i !== idx))}
            onDownloadFile={() => { }}
            onOpenFile={idx => {
              // web 端：高亮并滚动到文件卡片
              if (typeof window !== 'undefined' && document) {
                setTimeout(() => {
                  const el = document.querySelector(`[data-file-idx="${idx}"]`);
                  if (el && typeof el.scrollIntoView === 'function') {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
                    el.classList.add('file-highlight');
                    setTimeout(() => el.classList.remove('file-highlight'), 1600);
                  }
                }, 100);
              }
              // 移动端可自定义跳转行为
            }}
          />
        )}

        {/* 预览 Modal */}
        {/* 图片预览模态框 - 使用统一的 ImagePreviewModal 组件 */}
        <ImagePreviewModal
          visible={previewVisible}
          items={previewItems}
          initialIndex={previewIndex}
          onClose={() => setPreviewVisible(false)}
        />

        <View
          data-chat-input="true"
          style={{
            width: "100%",
            height: 200,
            flexDirection: "column",
            paddingHorizontal: 12,
            paddingVertical: 8,
            backgroundColor: white,
            borderTopWidth: pendingFiles.length > 0 ? 0 : 1,
            borderTopColor: "#E5E5E5",
            zIndex: 1001, // 确保输入区域在遮罩层之上
            position: 'relative',
          }}
        >
          {/* 上方：图标行 */}
          <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
            {/* 表情图标 */}
            <Pressable
              ref={emojiIconRef}
              style={[
                styles.inputIconBox,
                showEmojiInput && styles.inputIconBoxActive
              ]}
              onPress={() => {
                // 测量图标位置
                emojiIconRef.current?.measure((x, y, width, height, pageX, pageY) => {
                  setEmojiIconPosition({
                    x: pageX,
                    y: pageY,
                    width: width,
                    height: height,
                  });
                });
                // 如果表情选择器已显示，再次点击则隐藏；否则显示
                if (showEmojiInput) {
                  closeAllPickers();
                } else {
                  closeAllPickers(); // 先关闭所有选择器
                  onPressInputIcon("emoji");
                }
              }}
            >
              <EmojiIcon
                width={24}
                height={24}
                fill={showEmojiInput ? color_main : undefined}
              />
            </Pressable>

            {/* 收藏表情图标 */}
            <Pressable
              ref={collectIconRef}
              style={[
                styles.inputIconBox,
                showCollectPicker && styles.inputIconBoxActive
              ]}
              onPress={() => {
                // 测量图标位置
                collectIconRef.current?.measure((x, y, width, height, pageX, pageY) => {
                  setCollectIconPosition({
                    x: pageX,
                    y: pageY,
                    width: width,
                    height: height,
                  });
                });
                // 如果收藏选择器已显示，再次点击则隐藏；否则显示（先关闭所有选择器）
                if (showCollectPicker) {
                  closeAllPickers();
                } else {
                  closeAllPickers(); // 先关闭所有选择器
                  setShowCollectPicker(true);
                }
              }}
            >
              <CollectIcon
                width={24}
                height={24}
                fill={showCollectPicker ? color_main : undefined}
              />
            </Pressable>

            {/* 文件图标 - 多选图片/视频/文档 */}
            {(props.showImage || props.showVideo || props.showFile) && (<Pressable
              style={styles.inputIconBox}
              onPress={onFilePicker}
            >
              <FileIcon
                width={24}
                height={24}
              />
            </Pressable>)}
          </View>

          {/* 下方：输入框 */}
          <View ref={inputContainerRef} style={{ flex: 1 }}>
            <TextInput
              ref={inputRef}
              placeholder={Str_SendMsgPlaceholder}
              editable={true}
              blurOnSubmit={false}
              multiline={true}
              scrollEnabled={true}
              selectionColor={color_main}
              onKeyPress={handleKeyPress}
              onTextInput={setInputNativeSelection}
              onContentSizeChange={onContentSizeChange}
              onFocus={() => {
                onTextInputFocus();
              }}
              onBlur={() => onTextInputBlur()}
              onSelectionChange={handleSelectionChange}
              placeholderTextColor={color_BEBEBE}
              style={[
                chatInputStyles.inputTarget,
                { lineHeight: lineHeight, flex: 1 },
              ]}
              value={text}
              onChangeText={onChangeText}
            />
          </View>
        </View>

        {/* 已移除键盘占位，输入框不再升起 */}

        {/* 气泡式表情选择器 */}
        <EmojiPickerModal
          visible={showEmojiInput}
          onClose={() => {
            // 只有手动关闭时才隐藏（点击遮罩层或外部区域）
            setEmojiIconPosition(undefined);
            onUpdateActiveType("input");
          }}
          onEmoji={handleInputEmoji}
          onPicture={handleInputPicture}
          iconPosition={emojiIconPosition}
        />

        {/* 气泡式收藏表情选择器 */}
        <CollectPickerModal
          visible={showCollectPicker}
          onClose={() => {
            setCollectIconPosition(undefined);
            setShowCollectPicker(false);
          }}
          onPicture={handleInputPicture}
          iconPosition={collectIconPosition}
        />

        {/* 群聊@弹窗 */}
        <Modal
          visible={modalVisible}
          statusBarTranslucent={true}
          onRequestClose={() => setModalVisible(false)}
          animationType="slide"
        >
          <TopicUserView
            title={Str_PeopleChoose}
            onClose={() => {
              setModalVisible(false);
            }}
            tid={props.topicId}
            onClick={selectedAttUser}
            filterSelf={true}
          />
        </Modal>
      </View>
    );
  }
);
